import React, { lazy, Suspense, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { getComponentModuleOrMissing } from "components/ComponentType";
import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { ProgressSpinner } from "primereact/progressspinner";
import { Instant } from "@js-joda/core";
import { useTimezone } from "hooks/useTimezone";
import ErrorBoundary from "../error-boundary/ErrorBoundary";
import { getAdjustedCurrentTime, getAdjustedCurrentTimeInstant } from "../../utils";
import { ComponentProvider, useComponentStoreContext } from "./ComponentProvider";

interface ComponentContextWrapperProps {
  component: IDashboardComponent;
  tabId: number;
  groupId: string;
}

export interface ComponentContextProps {
  hideHeader?: boolean;
  from?: string;
  to?: string;
  referenceTime?: string;

  instantFrom?: Instant;
  instantTo?: Instant;
  instantReferenceTime?: Instant;
}

const ComponentContextWrapperInner = ({ component, tabId, groupId }: ComponentContextWrapperProps) => {
  const tz = useTimezone();

  const getTabContextConfig = useDashboardStore(state => state.getTabContextConfig);
  const refreshKey = useDashboardStore(state => state.refreshKey);

  const store = useComponentStoreContext();

  const { setComponentId } = store!.getState();
  const { setGroupId } = store!.getState();

  const tabContextConfig = getTabContextConfig(tabId) || {};

  const legacyTimes = getAdjustedCurrentTime(tabContextConfig.config || {}, tz.company);
  const instantTimes = getAdjustedCurrentTimeInstant(tabContextConfig.config || {}, tz.company);

  const mergedConfig: ComponentContextProps = {
    ...component.config,
    hideHeader: component.hideHeader,

    referenceTime: tabContextConfig.referenceTime || legacyTimes.referenceTime,
    from: tabContextConfig.from || legacyTimes.dateFrom,
    to: tabContextConfig.to || legacyTimes.dateTo,

    instantReferenceTime: tabContextConfig.instantReferenceTime || instantTimes.referenceTime,
    instantFrom: tabContextConfig.instantFrom || instantTimes.dateFrom,
    instantTo: tabContextConfig.instantTo || instantTimes.dateTo,
  };

  const LazyComponent = useMemo(() => {
    return lazy(async () => {
      const loadedModule = await getComponentModuleOrMissing(component.type);
      return { default: loadedModule.default };
    });
  }, [component.type]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: `1000px 0px`,
  });

  setComponentId(component.id);
  setGroupId(groupId);

  const shouldLoad = component.config?.reportMode || inView;

  const errorMsg = component.type === "NOT_SUPPORTED" ? T("error.component-is-not-supported") : undefined;

  return (
    <ErrorBoundary errorMessage={errorMsg}>
      <div ref={ref} className="h-full">
        {shouldLoad && (
          <Suspense fallback={<ProgressSpinner />}>
            <LazyComponent key={refreshKey} {...mergedConfig} />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};

const ComponentContextWrapper = React.memo(ComponentContextWrapperInner);

export default function WrappedComponentContextWrapper(props: ComponentContextWrapperProps) {
  return (
    <ComponentProvider initialState={{ componentId: props.component.id, groupId: props.groupId }}>
      <ComponentContextWrapper {...props} />
    </ComponentProvider>
  );
}
